/**
 * THE CHART BRUSH, AS A SCREEN READER RECEIVES IT.
 *
 * recharts 2.15.4 renders each brush traveller as `role="slider"` with
 * `tabIndex=0` — a focusable, keyboard-operable control — and then describes it
 * wrongly in two separate ways (`node_modules/recharts/es6/cartesian/Brush.js`):
 *
 *   1. THE NAME IS BUILT FROM A FIELD WE DO NOT HAVE. `:370` composes
 *      `"Min value: " + data[startIndex]?.name + ", Max value: " +
 *      data[endIndex]?.name`. Our rows are `{ key, label, value, ... }` with no
 *      `name`, so both handles announce the literal string
 *      **"Min value: undefined, Max value: undefined"**. `dataKey` does not help:
 *      the label is composed from a hardcoded `.name` and never reads `dataKey`.
 *      The only prop that changes it is `ariaLabel` — and that is ONE string
 *      shared by both travellers, which is why this module also writes the two
 *      names onto the nodes.
 *   2. THE VALUE IS A PIXEL. `:376` sets `aria-valuenow` to `travellerX`, the
 *      traveller's x-position in SVG user units (`:363`), and sets no
 *      `aria-valuemin`, `aria-valuemax`, `aria-valuetext` or
 *      `aria-orientation` at all. A slider with a naked coordinate and no range
 *      is what the QA snapshot recorded as a stray "85" and "1000" next to an
 *      unlabelled node.
 *
 * ── WHY THIS PATCHES THE DOM RATHER THAN PASSING PROPS ──
 *
 * There is no prop path for any of it. `Brush` accepts `ariaLabel` and nothing
 * else ARIA-shaped, the traveller `Layer` is built inside the library, and a
 * `<Brush>` must be a DIRECT child of the chart (recharts finds it by type), so
 * it cannot be wrapped in a component of ours. What makes the patch stable is
 * that React only writes an attribute whose PROP VALUE changed since the last
 * render: `aria-valuemin`/`aria-valuemax`/`aria-valuetext`/`aria-orientation`
 * are never rendered by recharts, so React has no record of them and never
 * touches them; `aria-label` is rendered once from a constant `ariaLabel` prop
 * and so is written once. Only `aria-valuenow` moves, and it moves exactly when
 * `onChange` fires — which is when this re-applies.
 *
 * ── KEYBOARD ──
 *
 * recharts handles ArrowLeft/ArrowRight and nothing else (`:383-389`), one
 * bucket per press, each press firing `onChange` and therefore a whole refetch.
 * Home/End/PageUp/PageDown are added here and commit ONCE, so a jump to the far
 * end of a 90-day range is one request rather than ninety.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

/** The one string recharts is given for both travellers. It is deliberately the
 *  generic one: the per-handle names are written by the effect below, and this
 *  is what shows in the window before it runs — never "undefined". */
export const BRUSH_ARIA_LABEL = 'Date range shown on this chart';

export interface BrushAriaAttributes {
  'aria-label': string;
  'aria-valuemin': string;
  'aria-valuemax': string;
  'aria-valuenow': string;
  'aria-valuetext': string;
  'aria-orientation': 'horizontal';
}

export interface BrushAriaPair {
  start: BrushAriaAttributes;
  end: BrushAriaAttributes;
}

/**
 * What the two travellers must announce, for a chart whose buckets carry
 * `labels` in axis order and whose window currently runs `startIndex..endIndex`.
 *
 * The bounds are PER HANDLE, which is the dual-thumb slider pattern: the start
 * handle cannot pass the end one, so its maximum is the bucket before it, and
 * the end handle's minimum is the bucket after the start. Announcing 0..n-1 on
 * both would tell a reader they can do something the control refuses.
 */
export const brushAria = (
  labels: string[],
  startIndex: number,
  endIndex: number
): BrushAriaPair | null => {
  const last = labels.length - 1;
  if (last < 1) return null;

  const start = Math.min(Math.max(startIndex, 0), last);
  const end = Math.min(Math.max(endIndex, 0), last);
  if (end <= start) return null;

  return {
    start: {
      'aria-label': 'Start of the date range shown on this chart',
      'aria-valuemin': '0',
      'aria-valuemax': String(end - 1),
      'aria-valuenow': String(start),
      'aria-valuetext': labels[start],
      'aria-orientation': 'horizontal',
    },
    end: {
      'aria-label': 'End of the date range shown on this chart',
      'aria-valuemin': String(start + 1),
      'aria-valuemax': String(last),
      'aria-valuenow': String(end),
      'aria-valuetext': labels[end],
      'aria-orientation': 'horizontal',
    },
  };
};

/** A page of buckets — a tenth of the range, never less than one. */
export const brushPageStep = (length: number): number => Math.max(1, Math.round(length / 10));

/**
 * Where a key press lands, given which handle has focus. Returns null when the
 * key is not ours (the arrows belong to recharts) or when the move is refused
 * because the handles would cross or the handle is already at its bound.
 */
export const brushKeyMove = (
  key: string,
  handle: 'start' | 'end',
  labels: string[],
  startIndex: number,
  endIndex: number
): { startIndex: number; endIndex: number } | null => {
  const last = labels.length - 1;
  if (last < 1) return null;

  const page = brushPageStep(labels.length);
  const current = handle === 'start' ? startIndex : endIndex;
  const lowest = handle === 'start' ? 0 : startIndex + 1;
  const highest = handle === 'start' ? endIndex - 1 : last;

  let next: number;
  switch (key) {
    case 'Home':
      next = lowest;
      break;
    case 'End':
      next = highest;
      break;
    case 'PageUp':
      next = current + page;
      break;
    case 'PageDown':
      next = current - page;
      break;
    default:
      return null;
  }

  next = Math.min(Math.max(next, lowest), highest);
  if (next === current) return null;

  return handle === 'start'
    ? { startIndex: next, endIndex }
    : { startIndex, endIndex: next };
};

const HANDLED_KEYS = ['Home', 'End', 'PageUp', 'PageDown'];

interface UseBrushAriaOptions {
  /** The `id` given to `ChartShell`, which puts `data-testid="panel-<id>"` on
   *  the panel — the only stable handle onto this chart's own travellers when
   *  two charts are on screen. */
  panelId: string;
  /** One axis label per bucket, in order. Must be referentially stable. */
  labels: string[];
  /** Commit a new window. Called once per gesture, not once per bucket. */
  onRange: (startIndex: number, endIndex: number) => void;
}

export interface BrushAriaHandlers {
  /** Hand to `<Brush ariaLabel={...}>`. */
  ariaLabel: string;
  /** Hand to `<Brush onChange={...}>` INSTEAD of calling `onRange` directly —
   *  it records where the handles are, which is what the announced value is
   *  read from. */
  onChange: (range: { startIndex?: number; endIndex?: number }) => void;
}

export const useBrushAria = ({
  panelId,
  labels,
  onRange,
}: UseBrushAriaOptions): BrushAriaHandlers => {
  const last = labels.length - 1;
  /* A brush with no gesture on it yet spans everything, which is what recharts
     itself defaults to. */
  const [range, setRange] = useState<[number, number]>([0, Math.max(last, 0)]);

  /* A refetch replaces the buckets, and recharts resets the brush to the new
     full extent (`Brush.js:543-556`, keyed on `data` identity). The announced
     value has to follow, or it describes the window before the last gesture. */
  useEffect(() => {
    setRange([0, Math.max(labels.length - 1, 0)]);
  }, [labels]);

  const attributes = useMemo(
    () => brushAria(labels, range[0], range[1]),
    [labels, range]
  );

  /* WHY AN OBSERVER AND NOT A PLAIN EFFECT.
     Two separate timing problems, one mechanism. The travellers are mounted by
     `ResponsiveContainer` after it has measured itself, so they do not exist
     when this panel's first effects run and no later render of the PANEL is
     guaranteed to follow; and `aria-valuenow` is React-managed, so it goes back
     to a pixel whenever recharts moves a handle. Observing the panel catches
     both — the nodes appearing, and an attribute being written back — and the
     read-before-write below is what stops it from re-triggering itself. */
  useEffect(() => {
    if (!attributes) return undefined;
    const panel = document.querySelector(`[data-testid="panel-${panelId}"]`);
    if (!panel) return undefined;

    const apply = () => {
      const nodes = panel.querySelectorAll('.recharts-brush-traveller');
      if (nodes.length < 2) return;
      const write = (node: Element, attrs: BrushAriaAttributes) => {
        Object.entries(attrs).forEach(([name, value]) => {
          if (node.getAttribute(name) !== value) node.setAttribute(name, value);
        });
      };
      write(nodes[0], attributes.start);
      write(nodes[1], attributes.end);
    };

    apply();
    const observer = new MutationObserver(apply);
    observer.observe(panel, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-label', 'aria-valuenow'],
    });

    return () => observer.disconnect();
  }, [panelId, attributes]);

  /* Delegated, for the same reason: the panel element is there from the first
     render, the travellers are not. */
  useEffect(() => {
    const panel = document.querySelector(`[data-testid="panel-${panelId}"]`);
    if (!panel) return undefined;

    const onKeyDown = (event: Event) => {
      const e = event as KeyboardEvent;
      if (!HANDLED_KEYS.includes(e.key)) return;
      const target = e.target as Element | null;
      const traveller = target?.closest?.('.recharts-brush-traveller');
      if (!traveller) return;
      const nodes = Array.from(panel.querySelectorAll('.recharts-brush-traveller'));
      const handle = nodes.indexOf(traveller) === 0 ? 'start' : 'end';
      /* Without this, Home/End/PageUp/PageDown scroll the page out from under
         the control the reader is standing on. */
      e.preventDefault();
      e.stopPropagation();
      const move = brushKeyMove(e.key, handle, labels, range[0], range[1]);
      if (!move) return;
      setRange([move.startIndex, move.endIndex]);
      onRange(move.startIndex, move.endIndex);
    };

    panel.addEventListener('keydown', onKeyDown);

    return () => panel.removeEventListener('keydown', onKeyDown);
  }, [panelId, labels, range, onRange]);

  const onChange = useCallback(
    (next: { startIndex?: number; endIndex?: number }) => {
      if (next?.startIndex == null || next?.endIndex == null) return;
      setRange([next.startIndex, next.endIndex]);
      onRange(next.startIndex, next.endIndex);
    },
    [onRange]
  );

  return { ariaLabel: BRUSH_ARIA_LABEL, onChange };
};
